/* ==========================================================================
   Luxury Cursor + Hover Sound Controller
   - Custom circular cursor: 18px default, 80px on .card-wrapper hover
   - Hover sound: plays once per mouseenter on buttons and nav controls
   ========================================================================== */

(function () {
  'use strict';

  /* ---- 1. ENVIRONMENT GUARDS ---- */

  /* Skip in Shopify theme editor */
  if (window.Shopify && window.Shopify.designMode) return;

  /* Skip on touch-only devices */
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  /* ========================================================================
     2. CUSTOM CURSOR
     ===================================================================== */

  var cursorEl = document.getElementById('lux-cursor');
  if (!cursorEl) return;

  var labelEl = cursorEl.querySelector('.lux-cursor__label');

  /* Keep the cursor in the browser top layer so it can appear over modal banners. */
  if (typeof cursorEl.showPopover === 'function') {
    try { cursorEl.showPopover(); } catch (error) {}
  }

  /**
   * Selectors for zones where the custom cursor should yield to the
   * native pointer — cookie banners, currency widgets, disclosure
   * popups, form inputs, iframes, and any Shopify app overlay.
   */
  var EXCLUSION_SELECTOR = [
    '.shopify-app-block',
    '.disclosure__list',
    '.disclosure__button',
    '.localization-form',
    'localization-form',
    '.header__localization',
    '[id*="currency"]',
    '[class*="currency"]',
    '.bpa-container',
    '.bpa-price-convert',
    '.locale-selectors',
    '.currency-converter',
    '.notification-banner',
    'iframe',
    'input',
    'select',
    'textarea',
    '.cart-drawer',
    '.drawer',
    '[data-shopify-overlay]'
  ].join(', ');

  /* Live coordinates — updated on every mousemove */
  var mouseX = -100;
  var mouseY = -100;
  var rafScheduled = false;
  var cursorHidden = false;

  /**
   * Apply position via transform (compositor thread — no layout thrash).
   * Called inside rAF so we batch DOM writes.
   */
  function applyCursorPosition() {
    cursorEl.style.transform = 'translate(' + (mouseX - 0) + 'px, ' + (mouseY - 0) + 'px) translate(-50%, -50%)';
    rafScheduled = false;
  }

  /**
   * Check if the hovered element falls within an exclusion zone.
   * If so, hide the custom cursor and restore the native one.
   */
  function isExcluded(el) {
    if (!el || !el.closest) return false;
    return !!el.closest(EXCLUSION_SELECTOR);
  }

  document.addEventListener('mousemove', function (e) {
    mouseX = e.clientX;
    mouseY = e.clientY;

    if (!rafScheduled) {
      rafScheduled = true;
      requestAnimationFrame(applyCursorPosition);
    }

    var target = e.target;

    if (isExcluded(target)) {
      if (!cursorHidden) {
        cursorEl.classList.add('lux-cursor--hidden');
        document.body.classList.remove('lux-cursor-active');
        cursorHidden = true;
      }
      /* Also collapse the expanded "VIEW MORE" state so the cursor
         doesn't flash large when re-entering the card area */
      cursorEl.classList.remove('lux-cursor--expanded');
    } else {
      if (cursorHidden) {
        cursorEl.classList.remove('lux-cursor--hidden');
        document.body.classList.add('lux-cursor-active');
        cursorHidden = false;
      }
      /* Reveal cursor on first move */
      if (cursorEl.classList.contains('lux-cursor--hidden')) {
        cursorEl.classList.remove('lux-cursor--hidden');
      }
    }
  }, { passive: true });

  /* Hide cursor when it leaves the window */
  document.addEventListener('mouseleave', function () {
    cursorEl.classList.add('lux-cursor--hidden');
  });

  document.addEventListener('mouseenter', function () {
    if (!cursorHidden) {
      cursorEl.classList.remove('lux-cursor--hidden');
    }
  });

  /* Activate: mark body so CSS hides system cursor */
  document.body.classList.add('lux-cursor-active');

  /* ---- Product card hover expansion ---- */

  /**
   * Selector for the product card zone that triggers cursor expansion.
   * Targets the media/image area of card wrappers — NOT the entire card
   * so text/buttons keep working normally.
   */
  var CARD_SELECTOR = '.card-wrapper';

  /**
   * Selector for currency converter elements inside product cards.
   * When hovering these, the cursor should NOT expand to "VIEW MORE"
   * because the user is interacting with the currency switcher,
   * not trying to navigate to the product page.
   */
  var CURRENCY_WIDGET_SELECTOR = [
    '[class*="currency"]',
    '[id*="currency"]',
    '.bpa-container',
    '.bpa-price-convert',
    '.locale-selectors',
    '.currency-converter',
    '.shopify-app-block'
  ].join(', ');

  /**
   * Check if an element is part of the currency converter widget.
   */
  function isCurrencyWidget(el) {
    if (!el || !el.closest) return false;
    return !!el.closest(CURRENCY_WIDGET_SELECTOR);
  }

  function handleCardEnter() {
    cursorEl.classList.add('lux-cursor--expanded');
  }

  function handleCardLeave() {
    cursorEl.classList.remove('lux-cursor--expanded');
  }

  /**
   * Attach listeners to all current and future product cards.
   * Uses event delegation on document to handle dynamically injected
   * cards (featured collections, quick-add reloads, infinite scroll).
   *
   * IMPORTANT: If the hovered element is inside a currency converter
   * widget, we do NOT expand the cursor — the user is interacting
   * with the currency switcher, not the product card link.
   */
  document.addEventListener('mouseenter', function (e) {
    if (e.target && e.target.closest && e.target.closest(CARD_SELECTOR)) {
      /* Skip expansion if hovering a currency widget */
      if (isCurrencyWidget(e.target)) {
        handleCardLeave(); /* collapse if it was expanded */
        return;
      }
      handleCardEnter();
    }
  }, true /* capture phase for reliable mouseenter delegation */);

  document.addEventListener('mouseleave', function (e) {
    if (e.target && e.target.closest && e.target.closest(CARD_SELECTOR)) {
      /* Only collapse if we're not still inside a card */
      var related = e.relatedTarget;
      if (!related || !related.closest || !related.closest(CARD_SELECTOR)) {
        handleCardLeave();
      }
    }
  }, true);


  /* ========================================================================
     3. BUTTON HOVER SOUND
     ===================================================================== */

  var soundUrl = (function () {
    var el = document.getElementById('lux-sound-source');
    return el ? el.getAttribute('data-src') : null;
  })();

  /* If no sound URL, skip the sound system but DON'T exit the IIFE —
     the cursor system above must keep running. */
  if (!soundUrl) return;

  /* Pre-create Audio instance (decoded once, reused) */
  var hoverAudio = new Audio(soundUrl);
  hoverAudio.preload = 'auto';
  hoverAudio.volume = 0.4;
  if (typeof hoverAudio.load === 'function') {
    hoverAudio.load();
  }

  /**
   * Play the hover sound.
   * Resets to 0 each time so rapid re-hovers retrigger cleanly.
   */
  function playHoverSound() {
    try {
      hoverAudio.pause();
      hoverAudio.currentTime = 0;
      var promise = hoverAudio.play();
      /* Suppress uncaught promise on browsers that block autoplay */
      if (promise && typeof promise.catch === 'function') {
        promise.catch(function () { /* silent — autoplay policy */ });
      }
    } catch (err) {
      /* Fail silently */
    }
  }

  /**
   * Hover sound target selector.
   * Covers theme button variants, plain buttons, and header/navbar controls.
   */
  var HOVER_SOUND_SELECTOR = [
    'button',
    '[role="button"]',
    '.button',
    'button.button',
    'a.button',
    '.button--primary',
    '.button--secondary',
    '.btn',
    '.header__menu-item',
    '.header__icon',
    '.header__heading-link',
    '.small-menu__item',
    '.small-menu__link',
    '.list-menu__item',
    '.list__social--item',
    '.list-social__item',
    '.list-social__link',
    '.mega-menu__link',
    '.menu-drawer__menu-item',
    '[data-lux-sound]'
  ].join(', ');

  /* Event delegation — handles dynamic DOM additions */
  document.addEventListener('mouseenter', function (e) {
    if (!e.target) return;
    var target = e.target;

    /* Direct match */
    if (target.matches && target.matches(HOVER_SOUND_SELECTOR)) {
      playHoverSound();
      return;
    }
    /* Ancestor match (e.g. hover child icon inside a button) */
    if (target.closest) {
      var hoverTarget = target.closest(HOVER_SOUND_SELECTOR);
      if (hoverTarget) playHoverSound();
    }
  }, true /* capture for reliable mouseenter delegation */);

})();
